import { qportalApi } from './qportal-api';
import AdminSession, { AdminSecurityError, cookieRequest } from './admin-session';
// A session result returned by a cookie endpoint is adopted immediately (memory token + marker).
const session = async (path, body) => AdminSession.adopt(await cookieRequest(path, body));
const credentialed = { withCredentials: true };
const AdminSecurityApi = {
    capability: async () => (await qportalApi.get('account/admin-security/capability')).data,
    loginPasskeyOptions: () => cookieRequest('account/admin-session/passkey/options'),
    loginPasskeyComplete: (challengeId, credentialJson) => session('account/admin-session/passkey/complete', { challengeId, credentialJson }),
    secondFactor: (challengeId, code, recoveryCode) => session('account/admin-session/second-factor', { challengeId, code, recoveryCode }),
    viewInvitation: (token) => cookieRequest('account/admin-invitation/view', { token }),
    invitationPasskeyOptions: (token) => cookieRequest('account/admin-invitation/passkey/options', { token }),
    invitationPasskeyComplete: (token, challengeId, credentialJson, name) => session('account/admin-invitation/passkey/complete', { token, challengeId, credentialJson, name }),
    invitationTotpBegin: (token, password) => cookieRequest('account/admin-invitation/totp/begin', { token, password }),
    invitationTotpComplete: (token, challengeId, code) => session('account/admin-invitation/totp/complete', { token, challengeId, code }),
    mine: async () => (await qportalApi.get('account/admin-security')).data,
    stepUpOptions: async (purpose) => (await qportalApi.post('account/admin-security/step-up/options', { purpose })).data,
    stepUpComplete: async (purpose, input) => (await qportalApi.post('account/admin-security/step-up/complete', { purpose, ...input })).data,
    // Credential changes may start a session (first authenticator of a legacy account): send cookies and adopt.
    addPasskeyOptions: async (stepUpGrant, currentPassword) => (await qportalApi.post('account/admin-security/passkeys/options', { stepUpGrant, currentPassword }, credentialed)).data,
    addPasskeyComplete: async (input) => {
        const data = (await qportalApi.post('account/admin-security/passkeys/complete', input, credentialed)).data;
        if (data?.accessToken)
            AdminSession.adopt(data);
        return data;
    },
    removePasskey: async (credentialId, stepUpGrant) => (await qportalApi.post(`account/admin-security/passkeys/${encodeURIComponent(credentialId)}/remove`, { stepUpGrant })).data,
    addTotpBegin: async (stepUpGrant, currentPassword) => (await qportalApi.post('account/admin-security/totp/begin', { stepUpGrant, currentPassword }, credentialed)).data,
    addTotpComplete: async (input) => {
        const data = (await qportalApi.post('account/admin-security/totp/complete', input, credentialed)).data;
        if (data?.accessToken)
            AdminSession.adopt(data);
        return data;
    },
    regenerateRecoveryCodes: async (stepUpGrant) => (await qportalApi.post('account/admin-security/recovery-codes', { stepUpGrant })).data,
    revokeMySession: async (sessionId) => (await qportalApi.post(`account/admin-security/sessions/${sessionId}/revoke`, {})).data,
    team: async () => (await qportalApi.get('admin/team')).data,
    invite: async (input) => (await qportalApi.post('admin/team/invitations', input)).data,
    resendInvitation: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/invitations/${id}/resend`, { stepUpGrant })).data,
    revokeInvitation: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/invitations/${id}/revoke`, { stepUpGrant })).data,
    disableMember: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/members/${id}/disable`, { stepUpGrant })).data,
    enableMember: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/members/${id}/enable`, { stepUpGrant })).data,
    revokeMemberSessions: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/members/${id}/sessions/revoke`, { stepUpGrant })).data,
    resetMemberAuthenticators: async (id, stepUpGrant) => (await qportalApi.post(`admin/team/members/${id}/reset-authenticators`, { stepUpGrant })).data
};
const backendCode = (value) => typeof value === 'string' && /^[A-Z][A-Z0-9_]*$/.test(value) ? value : undefined;
// The backend code wins. Axios sets its own error.code (e.g. ERR_BAD_REQUEST) on every HTTP error, so it is
// never used; cookie requests throw AdminSecurityError, which already carries the backend code.
export const errorCode = (error) => {
    if (error instanceof AdminSecurityError)
        return error.code;
    const data = error?.response?.data;
    return (backendCode(data?.code) ||
        (error?.response?.status === 429 ? 'RATE_LIMITED' : undefined) ||
        backendCode(data?.title) ||
        'REQUEST_FAILED');
};
// Seconds until a rate-limited request may be retried: from the JSON body, else the (CORS-exposed) Retry-After header.
export const retryAfterSeconds = (error) => {
    const candidates = [
        error instanceof AdminSecurityError ? error.retryAfterSeconds : undefined,
        error?.response?.data?.retryAfterSeconds,
        error?.response?.headers?.['retry-after']
    ];
    for (const value of candidates) {
        const seconds = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseInt(value, 10) : NaN;
        if (Number.isFinite(seconds) && seconds > 0)
            return seconds;
    }
    return undefined;
};
// The wait can be seconds (endpoint limit) or up to an hour (per-account limit); say how long instead of guessing.
export const rateLimitedMessage = (error) => {
    const seconds = retryAfterSeconds(error);
    if (!seconds)
        return 'Too many attempts. Please wait and try again later.';
    if (seconds < 90)
        return `Too many attempts. Try again in ${Math.max(1, Math.ceil(seconds))} seconds.`;
    const minutes = Math.ceil(seconds / 60);
    return `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};
export default AdminSecurityApi;
